import { useAuth } from "../../store/AuthContext/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import AppHeader from "../../components/AppHeader/AppHeader";
import ApiManager from "../../api/ApiManager";
import Paywall from "../../components/Paywall/Paywall";

export default function BaseAppLayout({ children, headerText }) {
  const { isAuthenticated, isInitialized, login, canAccessApp } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const api = useMemo(() => new ApiManager(), []);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { getCookie } = useAuth();

  // Funzione per aprire il Paywall
  const handleUpgradeClick = () => {
    setIsUploadModalOpen(true);
  };

  // Handle sidebar toggle state
  const handleSidebarToggle = (collapsed) => {
    setIsSidebarCollapsed(collapsed);
  };

  const exchangeCodeForToken = useCallback(
    async (code) => {
      try {
        const body = new URLSearchParams({
          grant_type: "authorization_code",
          client_id: "bo24stke7qeq3l76saqttte3j",
          client_secret: "jf4njijdjir4hoj00u5v7lai9a191o670a4t9p3vf7j35kfcli0",
          code: code,
          redirect_uri: "https://getathena.app/client/library",
        });

        const response = await fetch(
          `https://${process.env.REACT_APP_COGNITO_DOMAIN}/oauth2/token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: body,
          }
        );

        const data = await response.json();

        if (data.access_token) {
          // Decodifica il token per ottenere il valore di 'sub'
          const payload = JSON.parse(atob(data.access_token.split(".")[1]));
          const userSub = payload.sub;

          if (!userSub) {
            console.error("Errore: 'sub' non trovato nel token.");
            return;
          }

          login({ data });
          // console.log("Token ottenuto con successo:", data.id_token);

          api
            .get(`/projects/fetch?user_sub=${userSub}`, {
              headers: {
                Authorization: `Bearer ${data.id_token}`,  // usa access_token
              },
            })
            .then((response) => {
              try {
                const data = response.data.body
                  ? JSON.parse(response.data.body)
                  : null;
                if (
                  !data ||
                  !data.projects ||
                  !Array.isArray(data.projects) ||
                  data.projects.length === 0
                ) {
                  console.error(
                    "Errore: Nessun progetto trovato o dati non validi.",
                    data
                  );
                  return;
                }
                const project = data.projects[0]; // Prende il primo progetto

                if (!project.pk || typeof project.pk !== "string") {
                  console.error("Errore: Il campo 'pk' non è valido.", project);
                  return;
                }

                // Rimuove 'project#' in modo sicuro
                const projectId = project.pk.startsWith("project#")
                  ? project.pk.slice(8)
                  : project.pk;

                if (!projectId || projectId.trim() === "") {
                  console.error(
                    "Errore: project_id è vuoto o non valido dopo il parsing."
                  );
                  return;
                }

                // Imposta il cookie solo se il projectId è valido
                document.cookie = `project_id=${projectId}; path=/`;

                // Reindirizza alla libreria
                navigate("/client/library", { replace: true });
              } catch (error) {
                console.error("Errore nel parsing della risposta API:", error);
              }
            })
            .catch((error) => {
              console.error("Errore nella chiamata API:", error);
              if (error.response.data.Message === "❌ Il tuo periodo di prova è terminato. Rinnova l’abbonamento.") {
                navigate("/pricing");
              }
            });
        } else {
          console.error("Errore nel recupero del token:", data);
          navigate("/login");
        }
      } catch (error) {
        console.error("Errore nella richiesta dei token:", error);
      }
    },
    [login, navigate]
  );

  const handleOnboarding = useCallback(async () => {
    const onboardingData =
      JSON.parse(localStorage.getItem("onboardingAnswers")) || {};
    try {
      if (Object.keys(onboardingData).length === 0) {
        console.log("Nessun dato di onboarding trovato, non eseguo l'API call.");
        return; // Se l'item non esiste, salta l'API call
      }

      console.log("Eseguo l'API call per l'onboarding con i dati:", onboardingData);
      console.log("Token ID:", getCookie("id_token"));
      api.post("/onboarding", onboardingData, {
        headers: {
          Authorization: `Bearer ${getCookie("id_token")}`,
        },
      })
        .then((response) => {
          console.log("Dati onboarding completato con successo:", response.data);
        })
        .catch((error) => {
          console.error("Errore durante la chiamata onboarding:", error);
        });

      const userSub = getCookie("auth_token");
      const idToken = getCookie("id_token");

      if (!userSub) {
        console.error("Errore: user_sub non trovato nel cookie.");
        return;
      }

      localStorage.removeItem("onboardingAnswers");
    } catch (error) {
      console.error("Errore durante la chiamata onboarding:", error);
    }
  }, [api]);

  useEffect(() => {
    if (!isInitialized) return;

    console.log("URL search params:", location.search);

    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    console.log("Authorization code:", code);

    if (isAuthenticated) {
      console.log("Already authenticated, procedo con onboarding");
      handleOnboarding();
      return;
    }

    if (code) {
      console.log("Chiamo exchangeCodeForToken con code", code);
      exchangeCodeForToken(code)
        .catch((e) => console.error("Errore in exchangeCodeForToken:", e));
    } else if (!isAuthenticated) {
      console.log("Non autenticato, reindirizzo a /login");
      navigate("/login");
    }
  }, [isAuthenticated, isInitialized, location, navigate, handleOnboarding, exchangeCodeForToken]);

  useEffect(() => {
    if (isInitialized && !canAccessApp) {
      // setIsUploadModalOpen(true); // Mostra il paywall
      navigate("/pricing");
    }
  }, [isInitialized, canAccessApp]);

  useEffect(() => {
    const interval = setInterval(() => {
      const authToken = getCookie("auth_token");
      const idToken = getCookie("id_token");

      if (!authToken || !idToken) {
        console.warn("Token scaduto o mancante. Effettuo logout...");
        navigate("/login");
      }
    }, 10000); // controlla ogni 10 secondi

    return () => clearInterval(interval); // pulizia all'unmount
  }, [getCookie, navigate]);

  const sidebarRef = useRef(null);
  const [sidebarHeight, setSidebarHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (sidebarRef.current) {
        setSidebarHeight(sidebarRef.current.offsetHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Get sidebar collapse state from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    if (savedState !== null) {
      setIsSidebarCollapsed(savedState === "true");
    }
  }, []);

  // Define sidebar dimensions
  const EXPANDED_SIDEBAR_WIDTH = 240; // pixels
  const COLLAPSED_SIDEBAR_WIDTH = 64; // estimated width when collapsed (w-fit)

  return (
    <div className="w-full h-screen flex flex-row bg-gray-50 overflow-hidden">
      <Sidebar
        ref={sidebarRef}
        onUpgradeClick={handleUpgradeClick}
        onSidebarToggle={handleSidebarToggle}
      />
      <div
        style={{
          width:
            window.innerWidth < 768
              ? "100%"
              : `calc(100% - ${isSidebarCollapsed
                ? COLLAPSED_SIDEBAR_WIDTH
                : EXPANDED_SIDEBAR_WIDTH
              }px)`,
        }}
        className="h-screen flex flex-col bg-gray-50"
      >
        <AppHeader text={headerText} onUpgradeClick={handleUpgradeClick} />
        <div
          className="flex-1 bg-gray-50 overflow-y-auto overflow-x-hidden"
          style={{
            paddingBottom: window.innerWidth < 768 ? `${sidebarHeight}px` : "",
          }}
        >
          {children}
        </div>
      </div>
      <Paywall
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
      />
    </div>
  );
}
